import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ModelSectionBase } from './model-section-base';
import { t } from '@vitesse/base-ui/i18n';
import { Models } from '@vitesse/proto/vitesse/v1/model_pb.js';
import { button, iconButton } from '@vitesse/base-ui/components/base-buttons';
import { classifyBindingTarget } from './binding-target';
import { checkWiringSave } from '../../ai-tools/model-check';
import { applyWrite, draftOf, specOf, upsertOf } from '../../ai-tools/model-draft';
import { portTypes } from '../../ai-tools/port-types';
import { SearchAutocomplete } from '../../shell/symbolGrid/search/SearchAutocomplete';
import { formatSearchInput } from '../../shell/symbolGrid/search/searchCommands';
import type { ControllerManager } from '../../managers/controller-manager';
import type { GatewayEntry } from '../../managers/gateway-entry';
import type { TreeSelectDetail, EntityType } from '../../shell/vitesse-tree';
import type { BackendClient } from '../../bridge/backend-client';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-icon';
import '../../shell/vitesse-tree';

/*
 * <vitesse-model-bindings> — the model's INPUT editor: connect each declared input
 * to a controller SYMBOL or another model's OUTPUT (the marketplace "function call"
 * — bind formal params to concrete floor entities). A model reads each input by
 * its name, and only the root's own wiring (`inputs`, Part 4 of
 * docs/hierarchical-models-step-3-plan.md) gives that name a value, so this
 * editor writes that wiring, masked to ["inputs"].
 *
 * Editing is GATED ON STOPPED. The model-runner re-applies a descriptor edit as a
 * fresh respawn (the wiring cannot hot-swap), so the editor is read-only while
 * running (with a "stop to edit" hint) and editable only when stopped — stop →
 * edit → start, no surprise restart.
 *
 * Each input row shows its current connection HUMAN-READABLY: the source's own glyph
 * (a controller vs a model) + the source name + the leaf. Editing opens a small
 * two-column picker: SOURCES left, that source's TARGETS right. Pick a source, pick
 * a target, Save.
 *
 * DELIBERATELY MINIMAL. It is a chooser, not a workspace: ONE row style (`.mb-item`)
 * serves both columns, and there is no nav tree and no symbol grid inside it — a
 * picker that embeds either is doing far too much for "pick one value".
 *
 * SEPARATION IS BY CONTENT, PLACEMENT AND STYLING — never by rules: columns are told
 * apart by a gap, groups by a dim caption, the search by its fill, the selection by
 * its tint. No boxes, no dividers.
 *
 * SAVE IS PER DIALOG: a pick stages inside the open dialog and Save/Discard sit in
 * its footer, applying that ONE connection. Nothing accumulates behind a scroll.
 *
 * Sources are the model's OWN gateway's: the model-runner resolves a path on this
 * box, so a cross-gateway path could not resolve (P4).
 */
type SymRow = { path: string; dataType: string; leaf: boolean };

/** Rows per fetched window. Wide enough that the viewport sits deep inside it, so
 *  the next window is already in hand long before you reach the edge. */
const WINDOW = 400;
/** How far the viewport must travel before we fetch again. Quantizing the target
 *  to a stride is what makes a DRAG cost one query per stride crossed instead of
 *  one per scroll event — the handler fires every frame. */
const STRIDE = 150;
/** Must match .mb-vrow's height (the grid's --vg-row-height). */
const ROW_H = 34;
/** The rows pane's height (see .mb-pick) — drives how many rows we render. */
const VIEWPORT_H = 420;

// Sentinels for the two blue slots in the dialog title (see blueSlots).
const S0 = '\u0001';
const S1 = '\u0002';

/** Only a controller (its symbols) or another model (its outputs) can feed an input. */
const BINDABLE: EntityType[] = ['controller', 'model'];

@customElement('vitesse-model-bindings')
export class ModelBindingsSection extends ModelSectionBase {
  @property({ attribute: false }) controllers?: ControllerManager;
  /** Org gateways — the picker's source pane is the shell's own tree. */
  @property({ attribute: false }) gateways: GatewayEntry[] = [];
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  /** Current SCXML — required to round-trip on save (ModelUpsert needs it). */
  @property() scxml = '';

  @state() private saving = false;
  /** The model-runner's own sentence when it refuses a write. Shown rather than swallowed: a
   *  connection that did not save must not look identical to one that did. */
  @state() private saveError = '';
  // ── picker (ONE input at a time; staged + saved inside the dialog) ──
  @state() private editing = ''; // input name whose picker is open ('' = closed)
  /** The dialog's pending pick — the full entity path, '' = unbound. */
  @state() private staged = '';
  /** Selected source in the picker: 'controllers/<cid>' | 'models/<mid>' | ''. */
  @state() private pickSource = '';
  @state() private query = '';
  @state() private symRows: SymRow[] = [];
  @state() private loadingSyms = false;
  /** Rows in `symRows` start at this index of the FULL match set. */
  @state() private winStart = 0;
  /** How many rows the indexer says match — the scrollbar's truth, not our page's. */
  @state() private total = 0;
  @state() private scrollY = 0;
  private searchTimer?: ReturnType<typeof setTimeout>;
  private fetchSeq = 0;
  /** Offset of the window currently in flight (-1 = none) — dedups a drag. */
  private pending = -1;
  /** The `/` command hints — the SAME autocomplete the symbol grid uses. */
  private ac: SearchAutocomplete | null = null;

  protected sectionHeading(): string {
    return t('model.bindings');
  }
  protected sectionClass(): string {
    return 'model-bindings-section';
  }

  private get editable(): boolean {
    return this.model?.status === 'stopped';
  }
  /** The path the root's wiring connects an input to now, or '' when it has none. */
  private pathOf(name: string): string {
    return this.model?.wiring?.inputs.find((p) => p.name === name)?.path ?? '';
  }
  /** The tree, scoped to THIS model's gateway: the model-runner resolves a path
   *  on this box, so a cross-gateway path could not resolve (P4). */
  private get ownGateways(): GatewayEntry[] {
    return this.gateways.filter((e) => e.gateway.deviceFingerprint === this.gatewayId);
  }
  /** The tree's node id for the picked source, so it highlights exactly the way the
   *  shell's tree highlights the open page. */
  private treeSelectedId(): string {
    if (this.pickSource.startsWith('controllers/')) return `controller-${this.gatewayId}-${this.pickSource.slice('controllers/'.length)}`;
    if (this.pickSource.startsWith('models/')) return `model-${this.gatewayId}-${this.pickSource.slice('models/'.length)}`;
    return '';
  }
  private onTreeSelect(d: TreeSelectDetail): void {
    this.selectSource(d.entityType === 'controller' ? `controllers/${d.id}` : `models/${d.id}`);
  }
  /** Render a TRANSLATED sentence whose interpolated slots come out blue. The
   *  string is interpolated with sentinels and then split — never assembled from
   *  fragments — so each locale keeps its own word order (German and Japanese put
   *  the verb last; a "verb + a + into + b" concatenation would mangle both). */
  private blueSlots(text: string, values: string[]) {
    const out: unknown[] = [];
    let buf = '';
    for (const ch of text) {
      const i = ch === S0 ? 0 : ch === S1 ? 1 : -1;
      if (i < 0) {
        buf += ch;
        continue;
      }
      out.push(buf);
      buf = '';
      out.push(html`<b>${values[i]}</b>`);
    }
    out.push(buf);
    return out;
  }

  /** The staged pick, as a human reads it — the source end of the title's mapping.
   *  The WHOLE symbol path, not its last segment: a segment is ambiguous the moment
   *  paths are nested (every channel under a controller ends in ".Value", so a
   *  last-segment title would say "Bind Value into …" for all of them). Long paths
   *  ellipsize; they never lie. */
  private stagedLeaf(): string {
    let m = this.staged.match(/^controllers\/[^/]+\/symbols\/(.+)$/);
    if (m) return m[1];
    m = this.staged.match(/^models\/[^/]+\/variables\/(.+)$/);
    return m ? m[1] : '';
  }
  /** The 'controllers/<id>' | 'models/<id>' prefix of a connected path (or ''). */
  private sourceOf(path: string): string {
    const c = path.match(/^(controllers\/[^/]+)\//);
    if (c) return c[1];
    const m = path.match(/^(models\/[^/]+)\//);
    return m ? m[1] : '';
  }

  // ── picker lifecycle ──────────────────────────────────────────────────────
  private openPicker(name: string): void {
    this.editing = name;
    this.staged = this.pathOf(name);
    this.pickSource = this.sourceOf(this.staged); // open on the current source
    this.query = '';
    this.resetWindow();
    this.symRows = [];
    if (this.pickSource.startsWith('controllers/')) void this.loadSymbols(0);
  }
  private closePicker(): void {
    this.editing = '';
    this.staged = '';
  }
  private selectSource(source: string): void {
    this.pickSource = source;
    this.query = '';
    this.resetWindow();
    this.symRows = [];
    if (source.startsWith('controllers/')) void this.loadSymbols(0);
  }

  private resetWindow(): void {
    this.pending = -1;
    this.symRows = [];
    this.winStart = 0;
    this.total = 0;
    this.scrollY = 0;
    const el = this.renderRoot.querySelector('.mb-rows');
    if (el) el.scrollTop = 0;
  }
  private debouncedLoad(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => void this.loadSymbols(0), 250);
  }

  /**
   * Fetch ONE WINDOW of the match set — never the whole catalog, and never a
   * fixed cap. The indexer linearizes server-side and answers `offset`, so a
   * controller with 50k symbols costs exactly the same as one with 50: a window
   * of rows plus a `totalMatching` for the scrollbar.
   *
   * (It used to ask for pageSize 80 and stop. That wasn't a paging strategy, it
   * was a truncation — the other 49,920 symbols simply weren't bindable.)
   */
  private async loadSymbols(offset: number): Promise<void> {
    const cid = this.pickSource.startsWith('controllers/') ? this.pickSource.slice('controllers/'.length) : '';
    if (!this.backend || !cid) {
      this.symRows = [];
      this.total = 0;
      return;
    }
    const seq = ++this.fetchSeq;
    this.loadingSyms = this.total === 0; // only the FIRST paint is a "loading"; scrolling must not flash
    try {
      const start = Math.max(0, offset);
      const page = await this.backend.api.fetchSymbolPage(this.gatewayId, cid, {
        fts: this.query,
        offset: start,
        pageSize: WINDOW,
      });
      if (seq !== this.fetchSeq) return; // a newer query/window won; drop this one
      this.winStart = start;
      this.symRows = page.rows.map((r) => ({
        path: r.path,
        dataType: r.dataType,
        leaf: r.childCount === 0,
      }));
      this.total = page.totalMatching || page.rows.length;
    } catch {
      if (seq !== this.fetchSeq) return;
      this.symRows = [];
      this.total = 0;
    } finally {
      if (seq === this.fetchSeq) {
        this.loadingSyms = false;
        this.pending = -1;
      }
    }
  }

  /**
   * The window offset we WANT for a given first-visible row.
   *
   * Centred on the viewport, so the rows either side are already loaded whichever
   * way you scroll next, and QUANTIZED to a stride — the scroll event fires every
   * frame, so an unquantized target would issue a fetch per frame and a drag would
   * hammer the indexer with windows it has already flown past.
   */
  private windowFor(first: number): number {
    const centred = first - Math.floor((WINDOW - VIEWPORT_H / ROW_H) / 2);
    const quantized = Math.floor(Math.max(0, centred) / STRIDE) * STRIDE;
    return Math.max(0, Math.min(quantized, Math.max(0, this.total - WINDOW)));
  }

  /** Scroll → keep the right window loaded, and fetch the NEXT one before the
   *  viewport needs it. */
  private onScroll(e: Event): void {
    const el = e.target as HTMLElement;
    this.scrollY = el.scrollTop;
    const want = this.windowFor(Math.floor(el.scrollTop / ROW_H));
    // already have it, or already asking for it
    if (want === this.winStart || want === this.pending) return;
    this.pending = want;
    void this.loadSymbols(want);
  }

  // ── the `/` hints ───────────────────────────────────────────────────────────
  /**
   * The grid's own SearchAutocomplete, not a copy of it.
   *
   * getSymbolData returns null ON PURPOSE: this view is SERVER-PAGED, so it holds a
   * window, never the catalog. Feeding it the window would make `/type:` suggest
   * "the types among the 400 rows I happen to hold" — the same lie as filtering
   * client-side. Dataless, it offers every command plus the DECLARED enum values
   * (/type: boolean|integer|object|…), which is the honest full set; the INDEXER
   * then answers the filter against every symbol (search.go).
   */
  private syncAutocomplete(): void {
    const wrap = this.renderRoot.querySelector('.mb-bar .vg-search-wrap') as HTMLElement | null;
    if (!wrap) {
      this.ac?.destroy();
      this.ac = null;
      return;
    }
    if (this.ac) return;
    this.ac = new SearchAutocomplete(wrap, {
      getSymbolData: () => null,
      onSelect: (completion: string) => {
        const input = this.renderRoot.querySelector('.mb-search-input') as HTMLInputElement | null;
        if (!input) return;
        const at = input.value.lastIndexOf('/'); // replace from the last '/' — the grid's splice
        input.value = (at >= 0 ? input.value.slice(0, at) : input.value) + completion;
        this.query = input.value;
        this.resetWindow();
        void this.loadSymbols(0);
        input.focus();
      },
      onClose: () => {},
    });
  }
  private onSearchInput(e: InputEvent): void {
    const input = e.target as HTMLInputElement;
    this.query = input.value;
    this.ac?.update(input.value, input.selectionStart ?? input.value.length, input.getBoundingClientRect());
    this.debouncedLoad();
  }
  /** mousedown, not click — it must beat the input's blur (the grid's own trick). */
  private onSearchClear(e: MouseEvent): void {
    e.preventDefault();
    this.query = '';
    this.ac?.hide();
    this.resetWindow();
    void this.loadSymbols(0);
    const input = this.renderRoot.querySelector('.mb-search-input') as HTMLInputElement | null;
    if (input) {
      input.value = '';
      input.focus();
    }
  }
  private onSearchKey(e: KeyboardEvent): void {
    // while the popup is open it owns arrows / enter / escape
    if (this.ac?.handleKeyDown(e)) e.preventDefault();
  }
  protected updated(changed: Map<string, unknown>): void {
    super.updated?.(changed as never);
    this.syncAutocomplete();
  }
  disconnectedCallback(): void {
    this.ac?.destroy();
    this.ac = null;
    super.disconnectedCallback();
  }

  /** The type of each path picked here, as the picker showed it: the wiring check reads it before a save. */
  private readonly pickedTypes = new Map<string, string>();
  /** STAGE a target under the selected source ('symbols/<path>' | 'variables/<out>').
   *  Staged, not saved — the dialog's footer commits it. */
  private pickTarget(suffix: string, type?: string): void {
    if (!this.pickSource) return;
    this.staged = `${this.pickSource}/${suffix}`;
    if (type) this.pickedTypes.set(this.staged, type);
  }
  /** Persist ONE input's connection ('' disconnects it). */
  private async commit(name: string, path: string): Promise<void> {
    // The model this save began on. The page can open another model in this element during a
    // read, so every read and the write use these, and never `this` after an await.
    const { modelId, models, scxml } = this;
    const m = this.model;
    if (!m || !models || !scxml) return;
    const shown = (): boolean => this.modelId === modelId && this.models === models;
    this.saving = true;
    this.saveError = '';
    try {
      // The root's wiring, whole, from the same read as the descriptor: a constant another input
      // holds goes back as it is typed.
      const source = await models.getSource(modelId);
      const base = source.descriptor ? draftOf(source, m) : null;
      // The mask replaces the whole map, so a relay that sends no descriptor would erase every
      // other input's wiring. Refuse, as models.write does.
      if (!base) {
        if (shown())
          this.saveError = 'This relay is older than the root wiring editor: it does not send the wiring to keep, so nothing was saved.';
        return;
      }
      const { draft, parts } = applyWrite(base, { scxml: base.scxml, inputs: { [name]: path ? { path } : null } });
      // The wiring check first (Part 4 of the Step 3 plan): a symbol whose type does not fit the
      // input is refused here, in the relay's words, before anything is sent. The relay checks
      // again, with every symbol's type, and with a group's children.
      const wiring = await checkWiringSave(
        {
          modelId,
          scxml,
          inputs: m.inputs,
          outputs: m.outputs,
          types: portTypes(m.iface),
          wiring: specOf(draft, { root: draft.scxml, templates: {} }).root.wiring ?? {},
        },
        models.all,
        (p) => this.pickedTypes.get(p),
      );
      if (wiring && wiring.refusing.length > 0) {
        if (shown()) this.saveError = wiring.refusing.join('; ');
        return;
      }
      // Say what we MEAN: this write sets the root's inputs, nothing else. The model-runner merges
      // it into the descriptor it owns, so the interface and the SCXML cannot change from here.
      const mask = parts.filter((p) => p !== 'scxml');
      if (mask.length > 0) await models.update(modelId, upsertOf(draft, mask, { name: m.name, desiredState: m.desiredState }));
    } catch (e) {
      // The refusal carries the reason the model-runner gave. Keeping it is the difference between
      // "that did not work, here is why" and a spinner that stops for no visible reason.
      if (shown()) this.saveError = e instanceof Error ? e.message : String(e);
    } finally {
      this.saving = false;
    }
  }
  private async saveStaged(): Promise<void> {
    const name = this.editing;
    if (!name) return;
    await this.commit(name, this.staged);
    this.closePicker();
  }
  /** Disconnect is its own explicit one-click action on a stopped model — no dialog. */
  private unbind(name: string): void {
    void this.commit(name, '');
  }

  // ── render ────────────────────────────────────────────────────────────────
  protected renderContent(): unknown {
    const m = this.model;
    if (!m) return nothing;
    if (!m.hasInterface || m.inputs.length === 0) {
      return html`${this.styles()}
        <div class="mb-empty">${t('model.noInputs')}</div>`;
    }
    return html`
      ${this.styles()}
      <div class="mb-list">
        ${m.inputs.map((name) => {
          // An input in the root's own wiring (Part 4): its path is edited here, and its constant is
          // set through the assistant.
          const wired = m.wiring?.inputs.find((p) => p.name === name);
          if (wired?.constant !== undefined) return this.renderWiredRow(name, wired.path, wired.constant);
          return this.renderRow(name, wired?.path ?? '');
        })}
      </div>
      ${this.saveError ? html`<div class="base-form-error">${this.saveError}</div>` : nothing} ${this.renderPicker()}
    `;
  }

  /** An input the model's own wiring feeds with a constant: read here, edited through the assistant, which writes the wiring. */
  private renderWiredRow(name: string, path: string, constant: string | undefined) {
    return html`<div class="mb-row" title=${t('model.bindingWired')}>
      <div class="mb-target">
        ${constant !== undefined ? html`<span class="mb-sym"><span class="mb-leaf">= ${constant}</span></span>` : this.renderTarget(path)}
      </div>
      <vitesse-icon class="mb-flow" name="arrow-right" .size=${12}></vitesse-icon>
      <span class="mb-name">${name}</span>
      <div class="mb-edit"></div>
    </div>`;
  }

  /** ONE line per input at ANY width, reading as the data FLOWS:
   *  `source → input`. Wide, the source carries its metadata (glyph + owner
   *  name + parent path); narrow, the row collapses to just the two IDs —
   *  `leaf → input` — by hiding the metadata spans in CSS, never by wrapping.
   *  The old layout folded into a two-line card below 420px, which spent two
   *  sparse rows per binding saying one row's worth. */
  private renderRow(name: string, path: string) {
    return html`<div class="mb-row">
      <div class="mb-target">${path ? this.renderTarget(path) : html`<span class="mb-unbound">${t('model.unbound')}</span>`}</div>
      <vitesse-icon class="mb-flow" name="arrow-right" .size=${12}></vitesse-icon>
      <span class="mb-name">${name}</span>
      <!-- Gate the ENTRY, not the dialog's Save: staging a set of connections
           and then being refused at the end is a worse refusal than not offering
           the pencil. The editable flag below is a DIFFERENT fact — a running
           model's wiring is locked regardless of who you are — so the two
           compose rather than merge. -->
      <div class="mb-edit">
        <vitesse-gated .rpc=${Models.method.update} hide>
          ${iconButton({
            icon: 'square-pen',
            title: this.editable ? t('common.edit') : t('model.bindingsLocked'),
            onClick: () => this.openPicker(name),
            size: 15,
            disabled: !this.editable,
          })}
          ${
            path
              ? iconButton({
                  icon: 'trash-2',
                  title: this.editable ? t('model.unbind') : t('model.bindingsLocked'),
                  onClick: () => this.unbind(name),
                  size: 15,
                  danger: true,
                  disabled: !this.editable,
                })
              : nothing
          }
        </vitesse-gated>
      </div>
    </div>`;
  }

  /** A dotted path is identified by its LAST segment, so it's printed in two
   *  parts: a dimmed parent prefix that absorbs the truncation, and the final
   *  segment, which never truncates. `Station_A3.Sensors.PartPresent` degrades to
   *  `…PartPresent`, never to `Station_A3.Sensors.Par…` — which would hide the one
   *  part that says which symbol this is. (A plain tail-ellipsis does exactly
   *  that; the CSS rtl trick fixes it but mangles array paths like `wordVals[80]`,
   *  so the split is both safer and prettier.) */
  private renderLeaf(path: string) {
    const i = path.lastIndexOf('.');
    // ONE .mb-sym group, so the flex gap of .mb-target never opens a hole
    // between the dimmed prefix and its leaf — they are one path.
    if (i < 0) return html`<span class="mb-sym"><span class="mb-leaf">${path}</span></span>`;
    return html`<span class="mb-sym"
      ><span class="mb-path">${path.slice(0, i + 1)}</span><span class="mb-leaf">${path.slice(i + 1)}</span></span
    >`;
  }

  /** Human-readable connection: the source's glyph (controller vs model) + name + leaf. */
  private renderTarget(path: string) {
    const target = classifyBindingTarget(path);
    if (target.kind === 'controller') {
      const cn = this.controllers?.all.find((c) => c.id === target.controllerId)?.name ?? target.controllerId;
      return html`<vitesse-icon name="cpu" .size=${12}></vitesse-icon><span class="mb-src">${cn}</span>${this.renderLeaf(target.symbol)}`;
    }
    if (target.kind === 'model') {
      const mn = this.models?.get(target.modelId)?.name ?? target.modelId;
      return html`<vitesse-icon name="git-compare-arrows" .size=${12}></vitesse-icon
        ><span class="mb-src">${mn}</span>${this.renderLeaf(target.variable)}`;
    }
    /*
     * NOT A PATH THIS PANEL CAN NAME — and it has to LOOK different.
     *
     * This panel names only paths that start `controllers/` or `models/`. A bare symbol name, which
     * subscribes to nothing, reaches here. So does an `integrations/` path, which the runner does
     * read. Rendered as a bare leaf it was
     * indistinguishable from a correct one — identical, in fact, once the narrow rule below hides a
     * valid target's glyph and owner. That is how an input connected to "Stacklight" instead of
     * "controllers/core/symbols/Stacklight" sat in plain sight on the one panel that shows this
     * wiring, reading as done.
     *
     * Its glyph is exempt from the narrow rule for the same reason the others are subject to it:
     * theirs is metadata about a connection that works, and this is the whole state of one that
     * does not.
     */
    return html`<vitesse-icon class="mb-warn" name="triangle-alert" .size=${12} title=${t('model.bindingNotAPath')}></vitesse-icon
      ><span class="mb-broken" title=${t('model.bindingNotAPath')}>${path}</span>`;
  }

  // ── the picker dialog ───────────────────────────────────────────────────────
  private renderPicker() {
    const open = !!this.editing;
    const dirty = this.staged !== this.pathOf(this.editing);
    const leaf = this.stagedLeaf();
    return html`<vitesse-dialog width="720px" .open=${open} @dialog-close=${() => this.closePicker()}>
      ${
        !open
          ? nothing
          : html` <!-- The mapping IS the title: "Bind <source> into <input>", both ends in
                 blue. It used to be a separate line under the header restating the
                 same thing — a whole row of vertical space for redundancy. -->
              <span slot="heading" class="mb-title">
                ${
                  leaf
                    ? this.blueSlots(t('model.bindMap', { source: S0, target: S1 }), [leaf, this.editing])
                    : this.blueSlots(t('model.bindTo', { name: S0 }), [this.editing])
                }
              </span>
              <div class="mb-pick">
                <!-- the shell's own tree: explore/expand/collapse, no sort, no Flows -->
                <vitesse-tree
                  class="mb-tree"
                  selectable
                  .gateways=${this.ownGateways}
                  .backend=${this.backend}
                  .selectableTypes=${BINDABLE}
                  .selectedId=${this.treeSelectedId()}
                  @tree-select=${(e: CustomEvent<TreeSelectDetail>) => this.onTreeSelect(e.detail)}
                ></vitesse-tree>
                ${this.renderTargets()}
              </div>
              <div slot="footer" class="mb-foot">
                ${button({ label: t('common.discard'), color: 'neutral', onClick: () => this.closePicker(), disabled: this.saving })}
                ${button({
                  label: t('common.save'),
                  color: 'blue',
                  onClick: () => void this.saveStaged(),
                  disabled: this.saving || !dirty,
                })}
              </div>`
      }
    </vitesse-dialog>`;
  }

  /** One list item, uniform for a controller symbol OR a model output.
   *
   *  A symbol's suffix carries its display path ("A.B.Part"), as every binding
   *  does (ModelDescriptor.symbol_paths): the AI and symbols.search use the
   *  same form. The model-runner encodes it where it meets NATS
   *  (Subjects.pathToSubject), as the RPC calls of the frontend encode it on the
   *  wire (writeSymbols / setPriority / lock / delete). */
  private targetItems(): { label: string; sub: string; suffix: string }[] {
    let items: { label: string; sub: string; suffix: string }[] = [];
    if (this.pickSource.startsWith('controllers/')) {
      items = this.symRows.map((r) => ({
        label: r.path,
        sub: r.dataType,
        suffix: `symbols/${r.path}`,
      }));
    } else if (this.pickSource.startsWith('models/')) {
      const m = this.models?.get(this.pickSource.slice('models/'.length));
      const q = this.query.toLowerCase();
      items = (m?.outputs ?? [])
        .filter((o) => !q || o.toLowerCase().includes(q))
        // The output's declared type, for the wiring check before a save.
        .map((o) => ({ label: o, sub: m?.iface?.outputs.find((x) => x.name === o)?.type ?? '', suffix: `variables/${o}` }));
    }
    return items;
  }

  /** THE right-hand view — ONE view regardless of where the values come from (a
   *  controller's symbols or a model's outputs). Styled from the symbol grid: its
   *  search row, its row metrics, its scrollbar — but a single NAME column, so
   *  there's no header to label, and a blue hover like the tree, because here a row
   *  is a thing you pick.
   *
   *  `/` COMMANDS WORK HERE, and they are the indexer's, not ours. The raw string
   *  goes down as `fts`; the indexer parses the same grammar the autocomplete emits
   *  (store/search.go) and answers over the WHOLE catalog. That is the only place it
   *  can be answered: this view holds a 600-row window of ~50k, so a client-side
   *  match() would silently mean "of the rows I happen to hold".
   *
   *  `/type=` is LOGICAL (boolean/integer/number/object/…) while SymbolQuery's
   *  data_type is the RAW single-valued PLC type — so "integer" = INT|DINT is not
   *  expressible as a data_type filter. That is why the language lives in the
   *  indexer and not in that field. */
  private renderTargets() {
    if (!this.pickSource)
      return html`<div class="mb-view">
        <div class="mb-hint">${t('model.pickSourceFirst')}</div>
      </div>`;
    const isCtrl = this.pickSource.startsWith('controllers/');
    const items = this.targetItems();
    return html`<div class="mb-view">
      <div class="vg-toolbar mb-bar">
        <div class="vg-search-wrap">
          <vitesse-icon class="vg-search-icon" name="search" .size=${14}></vitesse-icon>
          <div class="vg-search-highlight">
            ${formatSearchInput(this.query).map((tok) => html`<span class="vg-token-${tok.type}">${tok.text}</span>`)}
          </div>
          <input
            class="vg-search-input mb-search-input"
            .value=${this.query}
            spellcheck="false"
            autocomplete="off"
            placeholder=${t('grid.searchPlaceholder')}
            @input=${(e: InputEvent) => this.onSearchInput(e)}
            @keydown=${(e: KeyboardEvent) => this.onSearchKey(e)}
            @focus=${(e: FocusEvent) => {
              const el = e.target as HTMLInputElement;
              this.ac?.update(el.value, el.selectionStart ?? el.value.length, el.getBoundingClientRect());
            }}
            @blur=${() => setTimeout(() => this.ac?.hide(), 150)}
          />
          ${
            this.query
              ? html`<vitesse-icon
                  class="vg-search-clear"
                  name="x"
                  .size=${14}
                  @mousedown=${(e: MouseEvent) => this.onSearchClear(e)}
                ></vitesse-icon>`
              : nothing
          }
        </div>
      </div>
      <div class="mb-rows" @scroll=${(e: Event) => this.onScroll(e)}>${isCtrl ? this.renderVirtual() : this.renderOutputs(items)}</div>
    </div>`;
  }

  /**
   * The VIRTUAL list — the whole match set, one window in the DOM.
   *
   * Height is `totalMatching * ROW_H`, so the scrollbar is the size of the real
   * catalog (50k symbols = 50k rows of scroll); only the ~12 rows on screen exist
   * as elements, positioned by index. The indexer already linearizes and answers
   * `offset`, so this is just the client half of a paging protocol that was
   * already there.
   *
   * A flat page carries BRANCHES as well as leaves (the indexer's flat mode is
   * every entry, not every leaf). A branch isn't a value, so it renders dimmed and
   * unclickable rather than being filtered out — filtering client-side would
   * desynchronise our offsets from the indexer's, and the scrollbar would lie.
   */
  private renderVirtual() {
    if (this.loadingSyms && this.symRows.length === 0) return html`<div class="mb-hint">${t('common.loading')}</div>`;
    if (this.total === 0) return html`<div class="mb-hint">${t('model.noSymbols')}</div>`;
    const first = Math.max(0, Math.floor(this.scrollY / ROW_H) - 4);
    const last = Math.min(this.total, first + Math.ceil(VIEWPORT_H / ROW_H) + 8);
    const rows = [];
    for (let i = first; i < last; i++) {
      const r = this.symRows[i - this.winStart];
      rows.push(
        r
          ? this.renderVirtualRow(r, i)
          : // in the gap between windows — hold the row's space so nothing jumps
            html`<div class="mb-vrow mb-vrow--ghost" style="transform:translateY(${i * ROW_H}px)"></div>`,
      );
    }
    return html`<div class="mb-spacer" style="height:${this.total * ROW_H}px">${rows}</div>`;
  }

  private renderVirtualRow(r: SymRow, i: number) {
    const suffix = `symbols/${r.path}`;
    const sel = this.staged === `${this.pickSource}/${suffix}`;
    if (!r.leaf) {
      // a branch: context, not a value — visible so the tree still reads, inert
      return html`<div class="mb-vrow mb-vrow--branch" style="transform:translateY(${i * ROW_H}px)">
        <span class="mb-vname">${r.path}</span>
      </div>`;
    }
    return html`<button
      class="mb-vrow ${sel ? 'sel' : ''}"
      style="transform:translateY(${i * ROW_H}px)"
      @click=${() => this.pickTarget(suffix, r.dataType)}
    >
      <span class="mb-vname">${r.path}</span>
    </button>`;
  }

  /** A model's declared outputs — a short known list, no windowing needed. */
  private renderOutputs(items: { label: string; sub: string; suffix: string }[]) {
    if (items.length === 0) return html`<div class="mb-hint">${t('model.noOutputs')}</div>`;
    return items.map(
      (it) =>
        html`<button
          class="mb-vrow ${this.staged === `${this.pickSource}/${it.suffix}` ? 'sel' : ''}"
          @click=${() => this.pickTarget(it.suffix, it.sub)}
        >
          <span class="mb-vname">${it.label}</span>
        </button>`,
    );
  }

  /** Light-DOM section: scope all rules under the section class. Theme custom
   *  props only (no hardcoded palette) per the frontend styling rules. */
  private styles() {
    return html`<style>
      /* One bordered box, hairline-separated 34px rows — the SAME row system as
         the symbol grid and the picker, not a card per binding. A card each cost
         two borders + padding + gap per row and folded to two lines when narrow;
         this is one line per binding, always. */
      .model-bindings-section .mb-list {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--border-color);
        border-radius: 8px;
        overflow: hidden;
      }
      .model-bindings-section .mb-row {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 34px;
        padding: 0 6px 0 10px;
        border-bottom: 1px solid var(--border-color);
      }
      .model-bindings-section .mb-row:last-child {
        border-bottom: none;
      }
      .model-bindings-section .mb-name {
        flex: 0 0 auto;
        font-size: 12px;
        color: var(--text-primary);
        font-family: 'SF Mono', ui-monospace, monospace;
        white-space: nowrap;
      }
      /* The source leads and hugs the left — natural width, shrinkable — so the
         whole sentence source → input reads as one left-anchored phrase and
         the actions keep the right edge (mb-edit's margin-left:auto). */
      .model-bindings-section .mb-target {
        flex: 0 1 auto;
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
        overflow: hidden; /* children truncate INSIDE the row, never over the actions */
        font-size: 12px;
        color: var(--text-primary);
      }
      .model-bindings-section .mb-target vitesse-icon {
        color: var(--blue);
        flex-shrink: 0;
      }
      /* the flow arrow: source ──▶ input */
      .model-bindings-section .mb-flow {
        color: var(--text-secondary);
        opacity: 0.6;
        flex-shrink: 0;
      }
      /* ── ONE type treatment for the whole sentence ─────────────────────────
         Everything is the same mono at the same size; the ONLY axis is tone —
         CONTEXT (owner name, ›, parent path) in --text-secondary, the two IDS
         (leaf, input name) in --text-primary. No per-span opacity, no font
         mixing: the row reads as one line of code with its identifiers lit. */
      .model-bindings-section .mb-src {
        color: var(--text-secondary);
        font-family: 'SF Mono', ui-monospace, monospace;
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      /* NARROW → the same line, stripped to the two IDs: leaf → input. The
         source's metadata (glyph, owner name + ›, dimmed parent path) hides;
         nothing wraps, nothing reorders. Sized against the SECTION's container
         (the base gives container-type), so it answers the panel being dragged
         narrow, not the viewport. */
      @container (max-width: 420px) {
        /* :not(.mb-warn) — what hides here is metadata about a binding that WORKS (whose glyph and
           owner are recoverable from the row's own leaf). An unresolvable target is not that row
           with less detail, it is a different state, so its glyph stays at every width. */
        .model-bindings-section .mb-target vitesse-icon:not(.mb-warn),
        .model-bindings-section .mb-src,
        .model-bindings-section .mb-path {
          display: none;
        }
      }
      .model-bindings-section .mb-src::after {
        content: ' ›';
        opacity: 0.5;
      }
      /* prefix + leaf are ONE path — grouped so the row's flex gap never opens
         a hole inside it; the prefix absorbs all the truncation (shrink 200 ⇒
         it collapses to an ellipsis long before the leaf loses a character) */
      .model-bindings-section .mb-sym {
        display: flex;
        align-items: baseline;
        flex: 0 1 auto;
        min-width: 0;
      }
      .model-bindings-section .mb-path {
        font-family: 'SF Mono', ui-monospace, monospace;
        color: var(--text-secondary);
        flex: 0 200 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      /* … so the identifying last segment always survives */
      .model-bindings-section .mb-leaf {
        flex: 0 0 auto;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: 'SF Mono', ui-monospace, monospace;
        color: var(--text-primary);
      }
      /* An unresolvable target: the danger tone the rest of the app uses for a destructive or
         broken state, on both the glyph and the value, so the row reads wrong at a glance rather
         than reading like every other row. */
      .model-bindings-section .mb-warn {
        color: var(--red);
        flex-shrink: 0;
      }
      .model-bindings-section .mb-broken {
        color: var(--red);
        font-family: 'SF Mono', ui-monospace, monospace;
        text-decoration: underline dotted currentColor;
        text-underline-offset: 3px;
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .model-bindings-section .mb-unbound {
        color: var(--text-secondary);
        opacity: 0.6;
        font-style: italic;
      }
      .model-bindings-section .mb-edit {
        display: flex;
        gap: 2px;
        flex-shrink: 0;
        margin-left: auto; /* actions own the right edge; the sentence hugs the left */
      }
      /* Save / Discard live in the DIALOG's footer — one binding at a time, right
         where the eye already is when you finish picking. */
      .mb-foot {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        flex-wrap: wrap;
      }
      .model-bindings-section .mb-empty {
        font-size: 12px;
        color: var(--text-secondary);
        opacity: 0.7;
      }

      /* ── the dialog ────────────────────────────────────────────────────────
         The mapping lives in the TITLE, so there's no context row under the header
         eating a line to say the same thing twice. */
      .mb-title {
        display: block;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .mb-title b {
        color: var(--blue);
        font-weight: 600;
        font-family: ui-monospace, 'SF Mono', monospace;
      }

      /* two panes, told apart by the gap */
      .model-bindings-section .mb-pick {
        display: flex;
        gap: 16px;
        height: min(52vh, 420px);
      }
      /* the shell's tree — it brings its own .tree-* styling; this just boxes it */
      .model-bindings-section .mb-tree {
        width: 200px;
        flex-shrink: 0;
        overflow: auto;
        border-radius: 6px; /* match the value view's corners */
      }

      /* ── the value view — the symbol grid's look, one NAME column ───────────
         Reuses the grid's own tokens (--vg-*), so the search row, row height and
         hairline read identically. .vg-row itself can't be reused: it's
         position:absolute + a column grid, welded to the virtualizer. */
      .model-bindings-section .mb-view {
        flex: 1;
        min-width: 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
        background: var(--vg-bg);
        border: 1px solid var(--vg-border-subtle);
        border-radius: 6px;
        overflow: hidden;
      }
      .model-bindings-section .mb-bar {
        flex-shrink: 0;
      }
      .model-bindings-section .mb-bar .vg-search-icon {
        color: var(--text-secondary);
        display: flex;
        align-items: center;
      }
      /* The input carries the grid's FULL search machinery — transparent input text,
         the .vg-search-highlight token overlay painting it in colour, the ✕, the
         ghost — all from the global vg- styles. No local overrides: an override here
         (this had color: var(--text-primary)) double-paints every character under
         the overlay. */

      .model-bindings-section .mb-rows {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
      }
      /* the grid's scrollbar, verbatim */
      .model-bindings-section .mb-rows::-webkit-scrollbar {
        width: 6px;
      }
      .model-bindings-section .mb-rows::-webkit-scrollbar-track {
        background: transparent;
      }
      .model-bindings-section .mb-rows::-webkit-scrollbar-thumb {
        background: var(--vg-border-subtle);
        border-radius: 3px;
      }
      /* the virtual runway — as tall as the WHOLE match set, so the scrollbar is
         the size of the real catalog while ~12 rows exist in the DOM */
      .model-bindings-section .mb-spacer {
        position: relative;
        width: 100%;
      }
      /* one row: the grid's metrics + hairline, the tree's BLUE hover (a row here is
         something you PICK, not something you read) */
      .model-bindings-section .mb-vrow {
        display: flex;
        align-items: center;
        width: 100%;
        height: var(--vg-row-height, 34px);
        padding: 0 12px;
        border: none;
        border-bottom: 1px solid var(--vg-border-subtle);
        background: transparent;
        color: var(--text-primary);
        font: inherit;
        font-size: 12px;
        text-align: left;
        cursor: pointer;
        transition: background 0.1s ease;
      }
      /* virtual rows are placed by index; the outputs list (no windowing) is static */
      .model-bindings-section .mb-spacer .mb-vrow {
        position: absolute;
        top: 0;
        left: 0;
        box-sizing: border-box;
      }
      /* a branch is context, not a value: visible, dimmed, inert */
      .model-bindings-section .mb-vrow--branch {
        color: var(--text-secondary);
        opacity: 0.55;
        cursor: default;
      }
      .model-bindings-section .mb-vrow--branch:hover {
        background: transparent;
      }
      /* a row whose window hasn't landed yet — holds its space, never jumps */
      .model-bindings-section .mb-vrow--ghost {
        cursor: default;
      }
      .model-bindings-section .mb-vrow--ghost:hover {
        background: transparent;
      }
      .model-bindings-section .mb-vrow:hover {
        background: rgb(from var(--blue) r g b / 0.12);
      }
      .model-bindings-section .mb-vrow.sel {
        background: rgb(from var(--blue) r g b / 0.18);
        color: var(--blue);
      }
      .model-bindings-section .mb-vname {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: ui-monospace, 'SF Mono', monospace;
      }
      .model-bindings-section .mb-hint {
        font-size: 12px;
        color: var(--text-secondary);
        opacity: 0.7;
        padding: 10px 12px;
      }

      /* Save / Discard live in the DIALOG's footer — one binding at a time, right
         where the eye already is when you finish picking. */
      .mb-foot {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        flex-wrap: wrap;
      }
    </style>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-model-bindings': ModelBindingsSection;
  }
}
